import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Star } from "lucide-react";
import { LINKS, INFO, IMAGES } from "@/lib/constants";

const lines = ["Dein Stil.", "Deine Klasse.", "Unser Handwerk."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-center pt-28 pb-10" style={{ background: "#080808" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-5 gap-14 items-center">
        <div className="lg:col-span-3">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="pre-headline mb-8"
            data-testid="hero-preheadline"
          >
            Premium Barbershop · Bochum-Wattenscheid
          </motion.p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.1]" data-testid="hero-headline">
            {lines.map((line, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + i * 0.2, duration: 0.8, ease: "easeOut" }}
                className={`block ${i === 2 ? "gold-text" : ""}`}
              >
                {line}
              </motion.span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-8 text-base md:text-lg max-w-lg"
            style={{ color: "#999999" }}
            data-testid="hero-subheadline"
          >
            Haarschnitt · Bartstyling · Hautpflege
            <br />
            <span className="font-display italic" style={{ color: "#F5F0E8" }}>Für den Mann, der Wert auf sich legt.</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href={LINKS.fresha} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="hero-booking-cta">
              Termin buchen
            </a>
            <a href="#leistungen" className="btn-ghost" data-testid="hero-services-cta">
              Unsere Leistungen
            </a>
          </motion.div>
        </div>

        <div className="lg:col-span-2 relative hidden md:block">
          <div className="gold-beam absolute -left-6 top-8 bottom-8 z-10" />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="relative overflow-hidden"
            style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }}
          >
            <motion.div style={{ y: imgY }}>
              <img
                src={IMAGES.hero}
                alt="Nabil bei der Arbeit – Premium Barbershop Bochum"
                className="ken-burns w-full h-[560px] object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 60%, rgba(8,8,8,0.6))" }} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: [0, 1.05, 1] }}
            transition={{ delay: 1.6, duration: 0.8 }}
            className="absolute -bottom-6 -left-8 px-5 py-4 flex items-center gap-2"
            style={{ background: "#181818", border: "1px solid rgba(201,168,76,0.35)" }}
            data-testid="hero-stats-badge"
          >
            <Star size={16} fill="#C9A84C" stroke="#C9A84C" />
            <span className="text-sm font-medium">4.9 · Bochum's Premium Barbershop</span>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="max-w-7xl mx-auto px-6 lg:px-10 w-full mt-20"
      >
        <div className="border-t border-b py-4 flex flex-wrap justify-between gap-3 text-xs sm:text-sm" style={{ borderColor: "rgba(201,168,76,0.15)", color: "#999999" }} data-testid="hero-hours-bar">
          <span>{INFO.hoursWeek} &nbsp;|&nbsp; {INFO.hoursSat}</span>
          <span>{INFO.address} · {INFO.city}</span>
        </div>
      </motion.div>
    </section>
  );
}
